import { useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { History, ReceiptText, ExternalLink, Plus, Trash2 } from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL;
const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`);
const METHODS = ["bank_transfer", "upi", "card", "cash", "cheque", "other"];

/* Phase G8/G13 — the ONE shared Historical / Ad-hoc document builder, used
   identically from Finance -> Create & Calculate and Customer 360 ->
   Finance (never two independent implementations). Explicitly NOT the
   default path for a normal current transaction (see
   GuidedDocumentCreator) — this is free-form by design, for migrating old
   records or a genuine exception with no modern Payment Plan behind it.
   Two record types:
   - "ad_hoc": today's pre-Phase-G free-form behaviour, unchanged, gated on
     finance.docs alone.
   - "historical_migration" (Phase G9, only offered to a user holding the
     dedicated finance.historical_migration capability): requires the
     original transaction date + a context note, may reference a product
     that isn't ordinarily selectable, and never guesses GST — an invoice
     with no resolvable rate is blocked server-side, never defaulted to 18%. */
export const HistoricalDocBuilder = ({ contact, opps, onCreated, canHistoricalMigration }) => {
  const [recordType, setRecordType] = useState("ad_hoc");
  const [docType, setDocType] = useState("manual_receipt");
  const [lines, setLines] = useState([{ label: "", amount: "" }]);
  const [oppId, setOppId] = useState("none");
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [originalDate, setOriginalDate] = useState("");
  const [contextNote, setContextNote] = useState("");
  const [gstRate, setGstRate] = useState("");
  const [gstSource, setGstSource] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);

  const isHistorical = recordType === "historical_migration";
  const total = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const taxable = isHistorical ? (gstRate ? total / (1 + Number(gstRate) / 100) : null) : total / 1.18;

  const setLine = (i, k, v) => setLines(lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));

  const create = async () => {
    if (lines.some((l) => !l.label.trim() || !(Number(l.amount) > 0))) {
      toast.error("Every line needs a description and an amount greater than 0");
      return;
    }
    if (isHistorical && (!originalDate.trim() || !contextNote.trim())) {
      toast.error("Historical records require the original date and a context note");
      return;
    }
    if (isHistorical && docType === "manual_invoice" && !gstRate) {
      toast.error("A historical Tax Invoice needs an explicit, authorised GST rate — never defaulted to 18%");
      return;
    }
    setBusy(true);
    try {
      const idemKey = `manualdoc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      let data;
      if (isHistorical) {
        ({ data } = await api.post("/finance/historical/document", {
          contact_id: contact.contact_id,
          opportunity_id: oppId !== "none" ? oppId : null,
          doc_type: docType === "manual_receipt" ? "historical_receipt" : "historical_invoice",
          lines: lines.map((l) => ({ label: l.label.trim(), amount: Number(l.amount) })),
          original_date: originalDate.trim(), context_note: contextNote.trim(),
          gst_rate: gstRate ? Number(gstRate) / 100 : null, gst_source: gstSource || null,
          payment: docType === "manual_receipt" ? { method, reference: reference || null, paid_date: originalDate.trim() } : null,
          idempotency_key: idemKey,
        }));
      } else {
        ({ data } = await api.post("/finance/manual-document", {
          contact_id: contact.contact_id,
          doc_type: docType,
          lines: lines.map((l) => ({ label: l.label.trim(), amount: Number(l.amount) })),
          opportunity_id: oppId !== "none" ? oppId : null,
          payment: docType === "manual_receipt" ? { method, reference: reference || null } : null,
          notes: notes || null,
          idempotency_key: idemKey,
        }));
      }
      setLastDoc(data);
      toast.success(`${data.doc_number} created — saved under ${data.kind === "receipt" ? "Receipts" : "Invoices"} and the customer's record`);
      setLines([{ label: "", amount: "" }]); setReference(""); setNotes("");
      setOriginalDate(""); setContextNote(""); setGstRate(""); setGstSource("");
      onCreated?.();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  return (
    <Card className="border-dashed" data-testid="manual-doc-builder">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading flex items-center gap-1.5">
          <History className="h-4 w-4" />Historical / Ad-hoc document
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[11px] text-muted-foreground">
          For migrating old records, backfilling documents from before SalesOS, or a genuine exception with no modern Payment Plan/payment behind it. For a normal current transaction, use <b>Generate document</b> instead — SalesOS derives the amounts there; here they're typed by hand.
        </p>
        {canHistoricalMigration && (
          <div className="flex gap-1 rounded-md border p-1 w-fit">
            <Button size="sm" variant={recordType === "ad_hoc" ? "default" : "ghost"} className="h-7 text-xs" data-testid="manual-doc-recordtype-adhoc" onClick={() => setRecordType("ad_hoc")}>Ad-hoc</Button>
            <Button size="sm" variant={isHistorical ? "default" : "ghost"} className="h-7 text-xs" data-testid="manual-doc-recordtype-historical" onClick={() => setRecordType("historical_migration")}>Historical migration</Button>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Document type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="manual-doc-type" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manual_receipt">Payment receipt</SelectItem>
                <SelectItem value="manual_invoice">Tax invoice{!isHistorical && " (GST-inclusive, 18%)"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Link to opportunity / product (optional)</Label>
            <Select value={oppId} onValueChange={setOppId}>
              <SelectTrigger data-testid="manual-doc-opportunity" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not linked</SelectItem>
                {(opps || []).map((o) => <SelectItem key={o.opportunity_id} value={o.opportunity_id}>{o.product_name || o.product_code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {isHistorical && (
          <div className="grid sm:grid-cols-2 gap-3 rounded-md border p-2.5" data-testid="manual-doc-historical-fields">
            <div className="space-y-1">
              <Label className="text-xs">Original transaction/document date *</Label>
              <Input data-testid="manual-doc-original-date" className="h-9" placeholder="e.g. 14 Jan 2022" value={originalDate} onChange={(e) => setOriginalDate(e.target.value)} />
            </div>
            {docType === "manual_invoice" && (
              <div className="space-y-1">
                <Label className="text-xs">GST rate % — required, never defaulted *</Label>
                <Input data-testid="manual-doc-historical-gst-rate" className="h-9" type="number" placeholder="e.g. 18" value={gstRate} onChange={(e) => setGstRate(e.target.value)} />
              </div>
            )}
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">Context note — why this is a historical record *</Label>
              <Textarea data-testid="manual-doc-context-note" rows={2} value={contextNote} onChange={(e) => setContextNote(e.target.value)} />
            </div>
            {docType === "manual_invoice" && (
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs">GST source / provenance (optional)</Label>
                <Input data-testid="manual-doc-gst-source" className="h-9" placeholder="e.g. historical Commercial Plan v1, or Finance-confirmed recollection" value={gstSource} onChange={(e) => setGstSource(e.target.value)} />
              </div>
            )}
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs">Line items (amounts incl. GST)</Label>
          {lines.map((l, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input data-testid={`manual-line-label-${i}`} className="h-9 flex-1 min-w-0" placeholder="Description — e.g. Booking amount (incl. GST)"
                value={l.label} onChange={(e) => setLine(i, "label", e.target.value)} />
              <Input data-testid={`manual-line-amount-${i}`} className="h-9 w-28 sm:w-36" type="number" placeholder="₹"
                value={l.amount} onChange={(e) => setLine(i, "amount", e.target.value)} />
              <Button data-testid={`manual-line-remove-${i}`} variant="ghost" size="icon" className="h-9 w-9 shrink-0" disabled={lines.length === 1}
                onClick={() => setLines(lines.filter((_, idx) => idx !== i))}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button data-testid="manual-line-add" variant="outline" size="sm" className="h-8 text-xs" onClick={() => setLines([...lines, { label: "", amount: "" }])}>
            <Plus className="h-3.5 w-3.5 mr-1" />Add line
          </Button>
        </div>
        {docType === "manual_receipt" && (
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Payment method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="manual-payment-method" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((mth) => <SelectItem key={mth} value={mth}>{mth.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Payment reference (optional)</Label>
              <Input data-testid="manual-payment-reference" className="h-9" placeholder="UTR / transaction ID" value={reference} onChange={(e) => setReference(e.target.value)} />
            </div>
          </div>
        )}
        {!isHistorical && (
          <div className="space-y-1">
            <Label className="text-xs">Note on document (optional)</Label>
            <Textarea data-testid="manual-doc-notes" rows={2} className="text-sm" placeholder="Shown on the PDF above the standard notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        )}
        <div className="rounded-md bg-muted/50 p-2.5 text-xs flex items-center gap-3 flex-wrap" data-testid="manual-doc-summary">
          <span>Total: <b className="text-sm">{inr(total)}</b></span>
          {docType === "manual_invoice" && total > 0 && taxable != null && (
            <span className="text-muted-foreground">Taxable {inr(taxable)} · GST ({isHistorical ? `${gstRate}%` : "18%"} incl.) {inr(total - taxable)} — split computed by SalesOS</span>
          )}
          {docType === "manual_invoice" && isHistorical && !gstRate && (
            <span className="text-red-600 dark:text-red-400">GST rate required — never defaulted to 18% for a historical record</span>
          )}
          {docType === "manual_receipt" && <span className="text-muted-foreground">Receipt acknowledges payment received — not the final tax invoice</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="manual-doc-create" disabled={busy || total <= 0} onClick={create}>
            <ReceiptText className="h-4 w-4 mr-1.5" />{busy ? "Creating…" : docType === "manual_receipt" ? "Create receipt" : "Create invoice"}
          </Button>
          {lastDoc && (
            <a href={`${backend}/api/finance/documents/${lastDoc.document_id}/pdf`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="h-9 text-xs" data-testid="manual-doc-open-pdf">
                <ExternalLink className="h-3.5 w-3.5 mr-1" />Open {lastDoc.doc_number} PDF
              </Button>
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
